import { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { BookOpen, CheckCircle, AlertCircle, Clock, Star, Award } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Course, Enrollment, CourseType } from "@/types/courseTypes";
import { getStudentProgress, subscribeToStudentProgress, updateProgress } from "@/utils/firebase";
import { useFirebase } from "@/contexts/FirebaseContext";
import { motion, AnimatePresence } from "framer-motion";

interface ProgressUpdate {
  id: string;
  userId: string;
  userName: string;
  courseId: string;
  courseName: string;
  completedItems: string[];
  totalItems: string[];
  progressPercentage: number;
  lastUpdated: string;
  instructorComments?: string;
  instructorId?: string;
  status: 'in_progress' | 'completed' | 'needs_improvement';
  totalHours?: number;
  hoursTaken?: number;
  instructorName?: string;
}

interface CourseProgress {
  courseId: string;
  courseName: string;
  courseType: CourseType;
  progressUpdates: ProgressUpdate[];
  enrollment?: Enrollment;
  totalHours?: number;
  hoursTaken?: number;
}

const StudentProgress = () => {
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const { currentUser } = useFirebase();
  const { toast } = useToast();

  useEffect(() => {
    if (!currentUser?.uid) return;
    
    const unsubscribe = subscribeToStudentProgress(
      currentUser.uid,
      (progressUpdates) => {
        const progressByCourseByCourse: Record<string, ProgressUpdate[]> = {};
        
        progressUpdates.forEach(update => {
          if (!progressByCourseByCourse[update.courseId]) {
            progressByCourseByCourse[update.courseId] = [];
          }
          progressByCourseByCourse[update.courseId].push(update);
        });
        
        const progress = Object.entries(progressByCourseByCourse).map(([courseId, updates]) => {
          const mostRecent = updates[0];
          const courseType: CourseType = courseId.startsWith('theory-') ? 'theory' : 'practical';
          
          return {
            courseId,
            courseName: mostRecent.courseName,
            courseType,
            progressUpdates: updates,
            totalHours: mostRecent.totalHours,
            hoursTaken: mostRecent.hoursTaken
          };
        });
        
        setCourseProgress(progress);
        setLoading(false);
      }
    );
    
    return () => unsubscribe();
  }, [currentUser]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_progress':
        return <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200">In Progress</Badge>;
      case 'completed':
        return <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200">Completed</Badge>;
      case 'needs_improvement':
        return <Badge variant="outline" className="bg-orange-50 text-orange-600 border-orange-200">Needs Improvement</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const filteredProgress = activeTab === "all" 
    ? courseProgress 
    : courseProgress.filter(p => p.courseType === activeTab);

  const getPracticalProgressPercentage = (course: CourseProgress) => {
    if (!course.totalHours) return 0;
    const hoursTaken = course.hoursTaken || 0;
    return Math.min(100, Math.round((hoursTaken / course.totalHours) * 100));
  };

  return (
    <DashboardLayout role="student">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8 space-y-4"
      >
        <h1 className="text-3xl font-bold text-gray-900">My Progress</h1>
        <p className="text-gray-600">Track your learning journey and see instructor feedback</p>
      </motion.div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="all">All Courses</TabsTrigger>
          <TabsTrigger value="theory">Theory Courses</TabsTrigger>
          <TabsTrigger value="practical">Practical Courses</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          {loading ? (
            <div className="space-y-6">
              {[...Array(2)].map((_, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="h-6 bg-gray-200 rounded w-1/3 animate-pulse"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                      <div className="h-8 bg-gray-200 rounded w-full animate-pulse"></div>
                      <div className="h-20 bg-gray-200 rounded w-full animate-pulse"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredProgress.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <BookOpen className="h-12 w-12 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900">No progress data available</p>
                <p className="text-gray-500 mt-1">
                  {activeTab === "all" 
                    ? "Enroll in a course and start completing lessons to track your progress"
                    : `You don't have any progress data for ${activeTab} courses yet`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <AnimatePresence>
              <div className="space-y-6">
                {filteredProgress.map((course) => {
                  const latestUpdate = course.progressUpdates[0];
                  const isPractical = course.courseType === 'practical';
                  
                  return (
                    <motion.div
                      key={course.courseId}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle>{course.courseName}</CardTitle>
                              <CardDescription>
                                {course.courseType === 'theory' ? 'Theory Course' : 'Practical Training'}
                              </CardDescription>
                            </div>
                            {latestUpdate && getStatusBadge(latestUpdate.status)}
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                          <div className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Overall progress</span>
                              <span className="font-medium">
                                {isPractical 
                                  ? `${getPracticalProgressPercentage(course)}%` 
                                  : `${latestUpdate.progressPercentage}%`}
                              </span>
                            </div>
                            <Progress 
                              value={isPractical 
                                ? getPracticalProgressPercentage(course) 
                                : latestUpdate.progressPercentage
                              } 
                              className="h-2" 
                            />
                            {isPractical ? (
                              <p className="text-sm text-gray-600">
                                {course.hoursTaken || 0} of {course.totalHours || 0} hours completed
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600">
                                {latestUpdate.completedItems.length} of {latestUpdate.totalItems.length} items completed
                              </p>
                            )}
                          </div>

                          {latestUpdate.instructorComments && (
                            <div className="bg-blue-50 p-4 rounded-md">
                              <div className="flex items-start space-x-3">
                                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
                                <div>
                                  <h4 className="font-medium text-gray-900">Instructor Feedback</h4>
                                  <p className="text-gray-700 mt-1">{latestUpdate.instructorComments}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {!isPractical && (
                            <div>
                              <h4 className="font-medium text-gray-900 mb-3">Recent Progress</h4>
                              <div className="space-y-3">
                                {course.progressUpdates.slice(0, 3).map((update, index) => (
                                  <div 
                                    key={update.id} 
                                    className="flex items-start space-x-3 p-3 rounded-md bg-gray-50"
                                  >
                                    <div className="flex-shrink-0">
                                      {index === 0 ? (
                                        <Clock className="h-5 w-5 text-blue-500" />
                                      ) : (
                                        <CheckCircle className="h-5 w-5 text-gray-400" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-gray-900">
                                        Progress updated to {update.progressPercentage}%
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(update.lastUpdated).toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {isPractical && (
                            <div className="bg-gray-50 p-4 rounded-md">
                              <h4 className="font-medium text-gray-900 mb-2">Practical Training Details</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span>Instructor:</span>
                                  <span className="font-medium">{latestUpdate.instructorName || "Assigned Instructor"}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Total Hours:</span>
                                  <span className="font-medium">{course.totalHours || 0} hours</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Hours Completed:</span>
                                  <span className="font-medium">{course.hoursTaken || 0} hours</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Hours Remaining:</span>
                                  <span className="font-medium">
                                    {(course.totalHours || 0) - (course.hoursTaken || 0)} hours
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                        
                        {course.courseType === 'theory' && latestUpdate.progressPercentage === 100 && latestUpdate.status !== 'completed' && (
                          <CardFooter>
                            <div className="w-full bg-green-50 p-4 rounded-md">
                              <div className="flex items-center gap-3">
                                <Award className="h-6 w-6 text-green-500" />
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-900">Course Completed!</h4>
                                  <p className="text-sm text-gray-700">
                                    Congratulations! You've completed all lessons in this course.
                                  </p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="bg-white"
                                  onClick={() => {
                                    toast({
                                      title: "Certificate Generated",
                                      description: "Your completion certificate has been generated and is available for download.",
                                    });
                                  }}
                                >
                                  Get Certificate
                                </Button>
                              </div>
                            </div>
                          </CardFooter>
                        )}
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </AnimatePresence>
          )}
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
};

export default StudentProgress;
